// src/services/api.ts
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import logger from '../utils/logger';
import { AIPrediction } from '../types/prediction';

// Configuration
// NOTE: Configure these URLs in your .env file (see .env.example)
// REACT_APP_RAILWAY_API_URL should point to your deployed Core-ingest backend
const RAILWAY_API_URL = process.env.REACT_APP_RAILWAY_API_URL || 'https://core-ingest-ingest.up.railway.app';
// Default to Bubble LIVE (no /version-test suffix). Anyone intentionally targeting
// the test database must set REACT_APP_BUBBLE_API_URL explicitly.
const BUBBLE_API_URL = process.env.REACT_APP_BUBBLE_API_URL || 'https://smartfilterpro-scaling.bubbleapps.io/api/1.1/wf';

// The Bubble site that owns the workflows above (live, or version-test when
// REACT_APP_BUBBLE_API_URL points there), for links to its pages, e.g. the
// forgot-password page. Derived so a dev build never sends people to the
// live site's pages for a test-database account.
export const BUBBLE_SITE_URL = BUBBLE_API_URL.replace(/\/api\/1\.1\/wf\/?$/, '');

// Bubble API Endpoints (configurable)
export const BUBBLE_ENDPOINTS = {
  LOGIN: '/react_password',
  REFRESH_TOKEN: '/app_refresh_token',
  GET_USER_DATA: '/get_user_data',
  GET_SUBSCRIPTION: '/get_subscription',
  GENERATE_LOGIN_REDIRECT: '/generate_login_redirect_token/',
  // Bubble backend workflow that ends the user's Bubble session(s) when they
  // sign out of the dashboard. Body: { user_id }. Called with the user's
  // Bubble access token as Bearer — see notifyBubbleLogout below.
  LOGOUT: '/react_logout',
};

// Security: Helper function to sanitize tokens for logging
function sanitizeToken(token: string | null | undefined): string {
  if (!token) return 'NULL/EMPTY';
  if (token.length <= 8) return '***';
  return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
}

// Security: Helper function to sanitize data objects for logging
function sanitizeData(data: any): any {
  if (!data) return data;
  if (typeof data !== 'object') return data;

  const sensitiveKeys = ['password', 'token', 'access_token', 'refresh_token', 'core_token', 'authorization'];
  const sanitized: any = Array.isArray(data) ? [] : {};

  for (const key in data) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof data[key] === 'object' && data[key] !== null) {
      sanitized[key] = sanitizeData(data[key]);
    } else {
      sanitized[key] = data[key];
    }
  }

  return sanitized;
}

// Validate configuration
if (!process.env.REACT_APP_RAILWAY_API_URL) {
  logger.warn(
    '⚠️  REACT_APP_RAILWAY_API_URL not configured in .env file.\n' +
    `   Using fallback URL: ${RAILWAY_API_URL}\n` +
    '   Please set REACT_APP_RAILWAY_API_URL in your .env file to connect to Core-ingest backend.\n' +
    '   See .env.example for configuration template.'
  );
}

if (!process.env.REACT_APP_BUBBLE_API_URL) {
  console.warn(
    '⚠️  REACT_APP_BUBBLE_API_URL not configured in .env file.\n' +
    `   Using the Bubble LIVE default: ${BUBBLE_API_URL}\n` +
    '   To target the Bubble version-test database, set REACT_APP_BUBBLE_API_URL explicitly.\n' +
    '   Note: Create React App inlines environment variables at BUILD time — changing the\n' +
    '   env var requires a rebuild to take effect.'
  );
}

// Token refresh callback - will be set by AuthContext.
// IMPORTANT: this callback is used by the Core (railway) 401 interceptor below,
// so it MUST resolve with the refreshed CORE token (not the Bubble access token).
// It resolves null when the refresh did not yield a new core token, in which
// case the interceptor skips the retry rather than send the wrong token class.
let tokenRefreshCallback: (() => Promise<string | null>) | null = null;

// Create axios instances
const railwayAPI: AxiosInstance = axios.create({
  baseURL: RAILWAY_API_URL,
  timeout: 30000, // 30 seconds - increased for Core-ingest backend operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set/update the auth token (called by AuthContext)
export function setRailwayAuthToken(token: string | null) {
  if (token) {
    railwayAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete railwayAPI.defaults.headers.common['Authorization'];
  }
}

// Function to register token refresh callback
export function setTokenRefreshCallback(callback: (() => Promise<string | null>) | null) {
  tokenRefreshCallback = callback;
}

const bubbleAPI: AxiosInstance = axios.create({
  baseURL: BUBBLE_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set Bubble access token (if needed for authenticated endpoints)
export function setBubbleAccessToken(token: string | null) {
  if (token) {
    bubbleAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete bubbleAPI.defaults.headers.common['Authorization'];
  }
}

// Response interceptor for error handling and automatic token refresh
railwayAPI.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    logger.error('🚨 Railway API Error Details:');
    logger.error('   URL:', originalRequest?.url);
    logger.error('   Base URL:', originalRequest?.baseURL);
    logger.error('   Status:', error.response?.status);
    logger.error('   Message:', error.message);
    logger.error('   Data:', error.response?.data);

    // Handle 401 Unauthorized - refresh token and retry
    if (error.response?.status === 401 && !originalRequest._retry && tokenRefreshCallback) {
      originalRequest._retry = true;

      try {
        logger.log('🔄 401 Unauthorized - attempting to refresh token...');
        // The callback resolves with the refreshed CORE token (or null if the
        // refresh response did not include one — in that case, skip the retry).
        const newCoreToken = await tokenRefreshCallback();

        if (newCoreToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newCoreToken}`;
          logger.log('✅ Core token refreshed, retrying request...');
          return railwayAPI(originalRequest);
        }

        logger.warn('⚠️  Token refresh did not return a new core token - skipping retry');
      } catch (refreshError) {
        logger.error('❌ Token refresh failed:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    if (error.message === 'Network Error') {
      logger.error('   ⚠️  This is usually caused by:');
      logger.error('      1. Wrong API URL in .env file');
      logger.error('      2. Core-ingest backend is not running');
      logger.error('      3. CORS issues');
      logger.error('      4. Backend is unreachable');
    }

    return Promise.reject(error);
  }
);

// Request interceptor for Bubble API (log all requests)
bubbleAPI.interceptors.request.use(
  (config) => {
    const authHeader = config.headers?.['Authorization'] as string | undefined;
    const token = authHeader ? sanitizeToken(authHeader.replace('Bearer ', '')) : 'NONE';

    logger.log('🌐 Bubble API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasAuth: !!authHeader,
      token: token,
      data: sanitizeData(config.data)
    });
    return config;
  },
  (error) => {
    logger.error('🚨 Bubble API Request Error:', error);
    return Promise.reject(error);
  }
);

bubbleAPI.interceptors.response.use(
  async (response) => {
    const requestUrl = response.config.url || '';
    const isRefreshEndpoint = requestUrl.includes('/app_refresh_token') || requestUrl.includes(BUBBLE_ENDPOINTS.REFRESH_TOKEN);
    const isUserDataEndpoint = requestUrl.includes('/get_user_data') || requestUrl.includes(BUBBLE_ENDPOINTS.GET_USER_DATA);

    // Check for nested status in multiple locations (Bubble may vary response format)
    const nestedStatus = response.data?.response?.status || response.data?.status;
    const nestedBody = response.data?.response?.body || response.data?.response?.Body || response.data?.body;

    logger.log('✅ Bubble API Response:', {
      status: response.status,
      url: requestUrl,
      hasData: !!response.data,
      hasResponse: !!response.data?.response,
      nestedStatus: nestedStatus,
      nestedBody: nestedBody,
      responseKeys: response.data ? Object.keys(response.data) : [],
      innerResponseKeys: response.data?.response ? Object.keys(response.data.response) : [],
      isRefreshEndpoint: isRefreshEndpoint,
      isUserDataEndpoint: isUserDataEndpoint
    });

    // Skip interceptor for refresh token requests (prevent infinite loops)
    if (isRefreshEndpoint) {
      logger.log('⏩ Skipping interceptor for refresh endpoint');
      return response;
    }

    // Check for nested 404 errors on user data endpoints
    // This indicates the user doesn't exist in Bubble or token doesn't have access - force re-login
    // Check both response.data.response.status and response.data.status for flexibility
    if (nestedStatus === 404 && isUserDataEndpoint) {
      logger.error('🚫 Bubble API returned soft 404 for user data - user not found or no access');
      logger.error('🚫 Response body:', nestedBody);
      const error: any = new Error('User not found in Bubble or access denied - please login again');
      error.response = { status: 404, data: response.data };
      error.config = response.config;
      return Promise.reject(error);
    }

    // Also check for soft 404 on subscription endpoints
    const isSubscriptionEndpoint = requestUrl.includes('/get_subscription') || requestUrl.includes(BUBBLE_ENDPOINTS.GET_SUBSCRIPTION);
    if (nestedStatus === 404 && isSubscriptionEndpoint) {
      logger.warn('⚠️  Bubble API returned soft 404 for subscription data - user may not have a subscription');
      // Don't reject - let the caller handle missing subscription gracefully
      // Just log the warning and return the response as-is
      return response;
    }

    return response;
  },
  (error: AxiosError) => {
    logger.error('🚨 Bubble API Response Error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

// ============================================
// RAILWAY DATABASE API CALLS
// ============================================

export interface Device {
  device_id: string;
  device_name: string;
  manufacturer: string;
  model: string;
  workspace_id: string;
  last_temperature: number;
  last_humidity: number;
  last_equipment_status: string;
  is_reachable: boolean;
  updated_at: string;
  filter_usage_percent?: number; // Filter usage percentage (0-100) calculated by backend
  timezone?: string; // Device timezone (e.g., "America/Los_Angeles")
  filter_target_hours?: number; // Target hours for filter replacement (e.g., 720)
  subscription?: any; // Per-device subscription data from Bubble
}

export interface DeviceStatus {
  device_id: string;
  device_name: string;
  is_reachable: boolean;
  last_temperature: number;
  current_equipment_status: string;
  last_seen_at: string;
  last_humidity: number;
}

export interface RuntimeSession {
  session_id: string;
  device_id: string;
  mode: string;
  equipment_status: string;
  started_at: string;
  ended_at: string | null;
  // Core returns runtime_seconds; duration_seconds is kept for older callers.
  runtime_seconds?: number | null;
  duration_seconds?: number;
}

export interface DailySummary {
  device_id: string;
  date: string;
  runtime_seconds_total: number;
  // HVAC mode breakdown (heat/cool/fan/auxheat) - for internal analysis
  runtime_seconds_heat?: number;
  runtime_seconds_cool?: number;
  runtime_seconds_fan?: number;
  runtime_seconds_auxheat?: number;
  runtime_seconds_unknown?: number;
  // Thermostat mode settings (what user SET) - for Operating Mode Distribution
  runtime_seconds_mode_heat?: number;
  runtime_seconds_mode_cool?: number;
  runtime_seconds_mode_auto?: number;
  runtime_seconds_mode_off?: number;
  runtime_seconds_mode_away?: number;
  runtime_seconds_mode_eco?: number;
  runtime_seconds_mode_other?: number;
  runtime_sessions_count: number;
  avg_temperature: number;
  avg_humidity: number;
  // 'ecobee_runtime_report' when Core set the day to Ecobee's own runtime
  // report (core-ingest src/services/runtimeReconciliation.ts)
  validation_source?: string | null;
}

export interface RegionAverage {
  region_prefix: string;
  date: string;
  avg_runtime_seconds: string;  // Backend returns as string
  avg_runtime_hours: string;     // Backend returns as string
  avg_temp: string;              // Backend returns as string
  avg_humidity: string;          // Backend returns as string
  updated_at: string;
}

export interface FilterPrediction {
  id: number;
  device_id: string;
  predicted_days_remaining: number;
  predicted_hours_remaining: number;
  predicted_usage_percent: string;
  anomaly_score: string;
  avg_daily_runtime_hours: string;
  region_avg_runtime_hours: string;
  recent_runtime_hours: string;
  runtime_trend_factor: string;
  filter_age_days: number;
  total_runtime_hours: string;
  confidence_score?: 'high' | 'medium' | 'low';
  confidence_reason?: string;
  created_at: string;
}

export interface DeviceSummary {
  ok: boolean;
  device_id: string;
  device_name: string;
  real_time: {
    total_runtime_hours: number;
    filter_runtime_hours: number;
    filter_usage_percent: number;
    filter_target_hours: number;
    hours_remaining: number;
    /** Part of filter_runtime_hours credited at the last reset rather than measured (a filter the user already had). */
    filter_hours_credited?: number;
    days_since_reset: number | null;
    last_event_ts: string;
    is_active: boolean;
    use_forced_air_for_heat: boolean;
  };
  summaries: {
    total_runtime_hours: number;
    total_sessions: number;
    avg_temperature: number;
    avg_humidity: number;
    days_recorded: number;
    earliest_date: string;
    latest_date: string;
  };
  prediction: AIPrediction | null;
  summary: {
    device_id: string;
    total_runtime_seconds: string;
    total_sessions: string;
    avg_temperature: string;
    avg_humidity: string;
    days_recorded: string;
    earliest_date: string;
    latest_date: string;
  };
}

// Get all devices for a user
export const getDevices = async (userId?: string): Promise<Device[]> => {
  const response = await railwayAPI.get('/devices', {
    params: { user_id: userId },
  });
  const devices = response.data.devices || [];

  // Map backend field names to frontend interface
  // Backend may return 'reachable' and/or 'is_reachable' — treat device as online if either is truthy
  return devices.map((device: any) => ({
    ...device,
    is_reachable: !!(device.reachable || device.is_reachable),
    last_temperature: Number(device.last_temperature) || 0,
    last_humidity: Number(device.last_humidity) || 0,
  }));
};

// Get device status — endpoint is keyed by the internal device_id.
export const getDeviceStatus = async (deviceId: string): Promise<DeviceStatus> => {
  const response = await railwayAPI.get(`/device-status/${deviceId}`);
  const data = response.data;

  // Map backend field names to frontend interface
  // Backend may return 'reachable' and/or 'is_reachable' — treat device as online if either is truthy
  return {
    device_id: data.device_id,
    device_name: data.device_name,
    is_reachable: !!(data.reachable || data.is_reachable),
    last_temperature: Number(data.last_temperature) || 0,
    current_equipment_status: data.current_equipment_status,
    last_seen_at: data.last_seen_at,
    last_humidity: Number(data.last_humidity) || 0,
  };
};

// ============================================
// DASHBOARD ASSISTANT (Core POST /assistant/*)
// ============================================

export interface AssistantTurn {
  role: 'user' | 'assistant';
  content: string;
}

export class AssistantError extends Error {
  constructor(public code: string) {
    super(code);
  }
}

// Ask the dashboard assistant. Core answers with Claude using the signed-in
// user's own thermostat data; `account` is the Bubble-side context the
// dashboard already shows (see utils/assistantContext.ts).
export const askAssistant = async (params: {
  messages: AssistantTurn[];
  deviceId?: string | null;
  account?: Record<string, unknown> | null;
}): Promise<{ reply: string; messageId: number }> => {
  try {
    const response = await railwayAPI.post('/assistant/chat', {
      messages: params.messages,
      device_id: params.deviceId || null,
      account: params.account || null,
    }, { timeout: 90_000 });
    return { reply: response.data.reply, messageId: response.data.message_id };
  } catch (err: any) {
    if (err?.response) throw new AssistantError(err.response.data?.error || `http_${err.response.status}`);
    throw new AssistantError('network');
  }
};

export const rateAssistantAnswer = async (messageId: number, rating: 1 | -1): Promise<void> => {
  await railwayAPI.post('/assistant/feedback', { message_id: messageId, rating });
};

// Get runtime sessions for a device
export const getRuntimeSessions = async (
  deviceId: string,
  startDate?: string,
  endDate?: string,
  limit?: number
): Promise<RuntimeSession[]> => {
  const response = await railwayAPI.get('/runtime-sessions', {
    params: {
      device_id: deviceId,
      start_date: startDate,
      end_date: endDate,
      // Core defaults to 100 newest sessions; a few days of short cycles can exceed that.
      limit,
    },
  });
  return response.data.sessions || [];
};

// Get daily summaries
export const getDailySummaries = async (
  deviceId: string,
  days: number = 30
): Promise<DailySummary[]> => {
  const response = await railwayAPI.get('/summaries/daily', {
    params: {
      device_id: deviceId,
      days,
    },
  });
  return response.data.summaries || [];
};

// Get region averages
export const getRegionAverages = async (
  regionPrefix: string,
  days: number = 90
): Promise<RegionAverage[]> => {
  // Backend endpoint: /region-averages?region_prefix=224&days=90
  // Response format: { ok: true, count: 2, data: [...] }
  const response = await railwayAPI.get('/region-averages', {
    params: {
      region_prefix: regionPrefix,
      days,
    },
  });
  return response.data.data || response.data.averages || [];
};

// Get latest filter delivery prediction from core-ingest database
export const getLatestPrediction = async (
  deviceId: string
): Promise<FilterPrediction | null> => {
  try {
    logger.log('📡 Fetching AI prediction from database for device:', deviceId);
    // Core-ingest queries the predictions table directly
    const response = await railwayAPI.get(`/predictions/${deviceId}/latest`);
    logger.log('📥 Prediction API Response:', response.data);

    // Backend returns { ok: true, prediction: {...} } on success
    // or { ok: false, error: "..." } when no prediction exists
    if (response.data?.ok === false) {
      // No prediction found - this is expected for new devices
      logger.log('ℹ️  No prediction available yet for device:', deviceId);
      return null;
    }

    // Extract the prediction object if ok is true
    if (response.data?.ok && response.data?.prediction) {
      return response.data.prediction;
    }

    // Fallback for direct prediction object (backward compatibility)
    if (response.data && !response.data.hasOwnProperty('ok')) {
      return response.data;
    }

    return null;
  } catch (error: any) {
    logger.error('🚨 Prediction API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      endpoint: `/predictions/${deviceId}/latest`
    });
    return null;
  }
};

// Get device summary with real-time filter data
export const getDeviceSummary = async (
  deviceId: string
): Promise<DeviceSummary | null> => {
  try {
    logger.log('📡 Fetching device summary with real-time data for device:', deviceId);
    const response = await railwayAPI.get(`/summaries/device/${deviceId}`);
    logger.log('📥 Device Summary API Response:', {
      ok: response.data?.ok,
      hasRealTime: !!response.data?.real_time,
      filterUsagePercent: response.data?.real_time?.filter_usage_percent,
      totalRuntimeHours: response.data?.real_time?.total_runtime_hours
    });

    if (response.data?.ok) {
      return response.data;
    }

    return null;
  } catch (error: any) {
    logger.error('🚨 Device Summary API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      endpoint: `/summaries/device/${deviceId}`
    });
    return null;
  }
};

// Get equipment events (raw event data)
export const getEquipmentEvents = async (
  deviceId: string,
  limit: number = 100
): Promise<any[]> => {
  const response = await railwayAPI.get('/equipment-events', {
    params: {
      device_id: deviceId,
      limit,
    },
  });
  return response.data.events || [];
};

// Health check
export const checkHealth = async (): Promise<any> => {
  const response = await railwayAPI.get('/health');
  return response.data;
};

// ============================================
// BUBBLE.IO API CALLS
// ============================================

export interface BubbleUser {
  userId: string;
  email: string;
  name?: string;
  subscription_status: boolean;
  filter_delivery_date?: string;
  ThermCount?: number;
}

// Get user data from Bubble
export const getBubbleUserData = async (userId: string): Promise<BubbleUser> => {
  const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_USER_DATA, {
    user_id: userId,
  });
  return response.data.response;
};

// Get subscription info from Bubble (user-level - legacy)
export const getSubscriptionInfo = async (userId: string): Promise<any> => {
  const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_SUBSCRIPTION, {
    user_id: userId,
  });
  return response.data.response;
};

// Get subscription info for a specific device/thermostat
export const getDeviceSubscription = async (deviceId: string, userId: string): Promise<any> => {
  const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_SUBSCRIPTION, {
    device_id: deviceId,
    user_id: userId,
  });
  return response.data.response;
};

// Parameters for generating a login redirect URL
export interface LoginRedirectParams {
  user_id: string;
  page: string;
  redirect_page?: string;
  index?: number;
  Therm?: string;
}

// Response from generate login redirect endpoint
export interface LoginRedirectResponse {
  redirect_url: string;
}

// Generate a login redirect URL for Bubble pages (auto-login)
export const generateLoginRedirect = async (params: LoginRedirectParams): Promise<LoginRedirectResponse> => {
  logger.log('🔗 generateLoginRedirect - Making request with params:', sanitizeData(params));
  const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GENERATE_LOGIN_REDIRECT, params);
  logger.log('🔗 generateLoginRedirect - Raw response.data:', response.data);
  logger.log('🔗 generateLoginRedirect - response.data.response:', response.data.response);
  return response.data.response;
};

// Tell Bubble the user signed out of the dashboard, so it can end their
// Bubble session(s) too. POSTs { user_id } to BUBBLE_ENDPOINTS.LOGOUT with the
// user's Bubble access token as Bearer, so the workflow can act on Current
// User rather than trusting the posted id.
//
// Deliberately a plain axios call, not bubbleAPI: bubbleAPI's 401 handling
// refreshes tokens and, when that fails, signs the user out — which would
// call this again. Best-effort: a failure is logged and never blocks the
// local sign-out, which has already happened by the time this resolves.
export const notifyBubbleLogout = async (userId: string, accessToken: string | null): Promise<boolean> => {
  try {
    await axios.post(
      `${BUBBLE_API_URL}${BUBBLE_ENDPOINTS.LOGOUT}`,
      { user_id: userId },
      {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }
    );
    logger.log('✅ Bubble logout recorded');
    return true;
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    logger.warn(`⚠️ Bubble logout call failed${status ? ` (HTTP ${status})` : ''}; dashboard sign-out continues`);
    return false;
  }
};

// Trigger filter reset (posts to core API with x-core-token header)
export const triggerFilterReset = async (deviceId: string, userId: string): Promise<any> => {
  const coreToken = localStorage.getItem('smartfilterpro_core_token');

  const response = await railwayAPI.post('/filter-reset', {
    device_id: deviceId,
    user_id: userId,
    source: 'manual',
  }, {
    headers: {
      'x-core-token': coreToken || '',
    },
  });

  return response.data;
};

// ============================================
// COMBINED DATA FETCHING
// ============================================

export interface DashboardData {
  devices: Device[];
  deviceStatuses: Map<string, DeviceStatus>;
  recentSessions: RuntimeSession[];
  dailySummaries: DailySummary[];
  userData: BubbleUser | null;
}

// Fetch all data needed for dashboard
export const fetchDashboardData = async (userId: string): Promise<DashboardData> => {
  try {
    // Fetch from Railway (parallel requests)
    const [devices, userData] = await Promise.all([
      getDevices(userId),
      getBubbleUserData(userId).catch(() => null), // Bubble is optional
    ]);

    // Get device statuses and subscriptions for all devices (parallel)
    const statusPromises = devices.map((d) =>
      getDeviceStatus(d.device_id).catch(() => null)
    );
    const subscriptionPromises = devices.map((d) =>
      getDeviceSubscription(d.device_id, userId).catch(() => null)
    );

    const [statuses, subscriptions] = await Promise.all([
      Promise.all(statusPromises),
      Promise.all(subscriptionPromises)
    ]);

    const deviceStatuses = new Map<string, DeviceStatus>();
    statuses.forEach((status, index) => {
      if (status) {
        deviceStatuses.set(devices[index].device_id, status);
      }
    });

    // Attach subscription data to each device
    devices.forEach((device, index) => {
      if (subscriptions[index]) {
        device.subscription = subscriptions[index];
      }
    });

    // Get recent sessions for primary device (if exists)
    let recentSessions: RuntimeSession[] = [];
    if (devices.length > 0) {
      recentSessions = await getRuntimeSessions(
        devices[0].device_id,
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // Last 7 days
        new Date().toISOString()
      ).catch(() => []);
    }

    // Get daily summaries
    let dailySummaries: DailySummary[] = [];
    if (devices.length > 0) {
      dailySummaries = await getDailySummaries(devices[0].device_id, 30).catch(() => []);
    }

    return {
      devices,
      deviceStatuses,
      recentSessions,
      dailySummaries,
      userData,
    };
  } catch (error) {
    logger.error('Error fetching dashboard data:', error);
    throw error;
  }
};

// ============================================
// RECAP DATA API (for filter recap emails/pages)
// ============================================

// Token validation response
export interface RecapTokenValidation {
  valid: boolean;
  device_id?: string;
  user_id?: string;
  expires_at?: string;
  reason?: 'not_found' | 'expired' | 'missing_token' | 'server_error';
}

// Full recap data response
export interface RecapData {
  // Device identification
  device_id: string;
  device_name: string;
  manufacturer: string;
  model: string;

  // Filter usage stats
  filter_days: number;
  filter_hours: number;
  filter_target_hours: number;
  filter_usage_percent: number;

  // Daily averages
  avg_daily_runtime_hours: number;
  region_avg_runtime_hours: number;

  // Comparison metrics
  efficiency_percent: number;
  efficiency_label: 'more efficient' | 'less efficient' | 'about average';
  days_vs_region_average: number;
  estimated_savings: number | null;

  // Environmental averages
  avg_temperature: number | null;
  avg_humidity: number | null;
  region_avg_temperature: number | null;
  region_avg_humidity: number | null;

  // HVAC mode breakdown (percentages)
  heating_percent: number;
  cooling_percent: number;
  fan_only_percent: number;

  // Recap page link
  recap_token: string;
  recap_url: string;
  token_expires_at: string;

  // Metadata
  generated_at: string;
}

// Validate a recap token (public endpoint - no auth required)
export const validateRecapToken = async (token: string): Promise<RecapTokenValidation> => {
  const response = await railwayAPI.get(`/recap-token/${token}`);
  return response.data;
};

// Get recap data by device ID (can use core_token OR recap_token for auth)
export const getRecapData = async (deviceId: string, recapToken?: string): Promise<RecapData> => {
  const headers: Record<string, string> = {};

  if (recapToken) {
    // Use recap token for public access (from email links)
    headers['x-recap-token'] = recapToken;
  } else {
    // Use core token for authenticated access (from dashboard)
    const coreToken = localStorage.getItem('smartfilterpro_core_token');
    if (coreToken) {
      headers['x-core-token'] = coreToken;
    }
  }

  const response = await railwayAPI.get(`/recap-data/${deviceId}`, { headers });
  return response.data;
};

// Get full recap data by token (two-step: validate token, then fetch data)
export const getRecapByToken = async (token: string): Promise<RecapData> => {
  // Step 1: Validate token and get device_id
  const validation = await validateRecapToken(token);

  if (!validation.valid || !validation.device_id) {
    const error: any = new Error(validation.reason || 'Invalid token');
    error.response = {
      status: validation.reason === 'expired' ? 410 : 404
    };
    throw error;
  }

  // Step 2: Fetch recap data using device_id (with recap token for auth)
  return getRecapData(validation.device_id, token);
};

// Export axios instances for direct use (e.g., in auth)
export { bubbleAPI, railwayAPI };

// ============================================
// SAFE DATA ACCESS HELPERS
// ============================================

export interface ThermostatData {
  hvac_modes?: string[] | null;
  fan_modes?: string[] | null;
  hvac_action?: string;
  thermostat_mode?: string;
  current_temperature?: number;
  target_temp_low?: number;
  target_temp_high?: number;
}

// Helper function to safely access hvac modes from nested device payloads
export function getSafeHvacModes(device: any): string[] {
  const modes = device?.hvac_modes
    || device?.payload_raw?.hvac_modes
    || device?.payload_raw?.payload_raw?.hvac_modes;

  if (Array.isArray(modes) && modes.length > 0) {
    return modes;
  }

  return ['auto', 'heat', 'cool', 'off'];
}

// Helper function to safely access fan modes from nested device payloads
export function getSafeFanModes(device: any): string[] {
  const modes = device?.fan_modes
    || device?.payload_raw?.fan_modes
    || device?.payload_raw?.payload_raw?.fan_modes;

  if (Array.isArray(modes) && modes.length > 0) {
    return modes;
  }

  return ['auto', 'on'];
}

// Normalize thermostat device data to ensure arrays are never empty/null
export function normalizeThermostatData(device: any): any {
  if (!device) return device;

  const normalized = { ...device };

  if (!normalized.hvac_modes || (Array.isArray(normalized.hvac_modes) && normalized.hvac_modes.length === 0)) {
    normalized.hvac_modes = ['auto', 'heat', 'cool', 'off'];
  }

  if (!normalized.fan_modes || (Array.isArray(normalized.fan_modes) && normalized.fan_modes.length === 0)) {
    normalized.fan_modes = ['auto', 'on'];
  }

  return normalized;
}

// Default export as a named object
const api = {
  getDevices,
  getDeviceStatus,
  getRuntimeSessions,
  getDailySummaries,
  getRegionAverages,
  getLatestPrediction,
  getDeviceSummary,
  getEquipmentEvents,
  checkHealth,
  getBubbleUserData,
  getSubscriptionInfo,
  getDeviceSubscription,
  generateLoginRedirect,
  triggerFilterReset,
  fetchDashboardData,
  // Recap APIs
  validateRecapToken,
  getRecapByToken,
  getRecapData,
};

export default api;
